import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import { useActiveBanners } from "@/hooks/useBanners";
import type { Banner } from "@/types/Banner";

const FALLBACK_SCROLL_TIME = 5;

export const BannerCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Detecta se é mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const { data: bannersData, isLoading } = useActiveBanners({
    type: isMobile ? "MOBILE" : "DESKTOP",
  });

  const banners = bannersData?.data || [];
  const bucketUrl = import.meta.env.VITE_BUCKET_URL || "";

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => {
      const bannersLen = banners.length;
      if (bannersLen === 0) return 0;
      return (prev + 1) % bannersLen;
    });
  }, [banners.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      const bannersLen = banners.length;
      if (bannersLen === 0) return 0;
      return (prev - 1 + bannersLen) % bannersLen;
    });
  }, [banners.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  // Auto-play usando scrollTime individual de cada banner
  useEffect(() => {
    if (banners.length <= 1 || isPaused) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const currentBanner = banners[currentIndex];
    const delay = (currentBanner?.scrollTime ?? FALLBACK_SCROLL_TIME) * 1000;

    timerRef.current = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
    }, delay);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [banners, currentIndex, isPaused]);

  // Reset index se banners mudar
  useEffect(() => {
    if (currentIndex >= banners.length && banners.length > 0) {
      setCurrentIndex(0);
    }
  }, [banners.length, currentIndex]);

  const handleBannerClick = (banner: Banner) => {
    if (banner.linkUrl) {
      window.open(banner.linkUrl, banner.linkTarget || "_blank");
    }
  };

  // Não renderiza se não houver banners ou estiver carregando
  if (isLoading || banners.length === 0) {
    return null;
  }

  const currentBanner = banners[currentIndex];

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl md:rounded-2xl shadow-lg group"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Container do Banner */}
      <div className="relative aspect-[16/9] md:aspect-[3/1] lg:aspect-[3.5/1] bg-gradient-to-r from-[hsl(var(--primary))]/10 to-[hsl(var(--secondary))]/10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBanner.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 flex items-center justify-center"
          >
            <img
              src={`${bucketUrl}/${currentBanner.key}`}
              alt={currentBanner.title}
              className={`max-w-full max-h-full object-contain ${currentBanner.linkUrl ? "cursor-pointer" : ""}`}
              onClick={() => handleBannerClick(currentBanner)}
              loading="lazy"
            />

            {/* Overlay com título (opcional) */}
            {currentBanner.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 md:p-6">
                <h3 className="text-white font-bold text-sm md:text-lg line-clamp-1">
                  {currentBanner.title}
                </h3>
                {currentBanner.description && (
                  <p className="text-white/80 text-xs md:text-sm line-clamp-1 mt-1">
                    {currentBanner.description}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Botões de navegação - apenas se houver mais de 1 banner */}
        {banners.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-70 md:opacity-0 md:group-hover:opacity-100"
              aria-label="Banner anterior"
            >
              <IoChevronBackOutline className="text-lg md:text-xl text-gray-800" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-70 md:opacity-0 md:group-hover:opacity-100"
              aria-label="Próximo banner"
            >
              <IoChevronForwardOutline className="text-lg md:text-xl text-gray-800" />
            </button>
          </>
        )}
      </div>

      {/* Indicadores de posição (dots) */}
      {banners.length > 1 && (
        <div className="absolute bottom-2 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 md:gap-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? "w-6 md:w-8 h-2 md:h-2.5 bg-white"
                  : "w-2 md:w-2.5 h-2 md:h-2.5 bg-white/50 hover:bg-white/70"
              }`}
              aria-label={`Ir para banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
